import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import RatingModal from '../common/RatingModal';
import './ContractDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getCurrencySymbol = (code) => {
  const symbols = { USD: '$', EUR: '€', GBP: '£' };
  return symbols[code] || code || '$';
};

const OwnerContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [hasRated, setHasRated] = useState(false);

  const getToken = () => localStorage.getItem('marilink_token');

  useEffect(() => {
    if (id && token) fetchContract();
  }, [id, token]);

  useEffect(() => {
    if (contract?.status === 'payment_released' && contract?._id) {
      checkIfRated();
    }
  }, [contract]);

  const checkIfRated = async () => {
    try {
      const t = getToken();
      const res = await fetch(`${API_BASE}/ratings/contract/${contract._id}`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      const data = await res.json();
      if (data.success && data.rating) setHasRated(true);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchContract = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.success) {
        setContract(data.contract);
      } else {
        setError(data.message || 'Failed to load contract');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    const map = {
      pending_payment: 'Active',
      payment_uploaded: 'Active',
      payment_verified: 'Active Contract',
      work_in_progress: 'Work In Progress',
      work_completed: 'Work Completed',
      payment_released: 'Completed'
    };
    return map[status] || status;
  };

  const handleConfirmComplete = async () => {
    if (!window.confirm('Confirm this work as complete? This will finalize the contract.')) return;
    try {
      const res = await fetch(`${API_BASE}/contracts/${id}/complete-work`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchContract();
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error confirming completion');
    }
  };

  const getVendorDisplayName = () => {
    if (!contract) return 'Not assigned';
    if (contract.vendorName && contract.vendorName.trim()) return contract.vendorName.trim();
    if (contract.vendorId && typeof contract.vendorId === 'object') {
      return contract.vendorId.company || contract.vendorId.name || contract.vendorId.email || 'Not assigned';
    }
    return 'Not assigned';
  };

  const getVendorId = () => {
    if (!contract) return null;
    if (contract.vendorId && typeof contract.vendorId === 'object') return contract.vendorId._id;
    return contract.vendorId;
  };

  if (loading) return (
    <div className="loader-container">
      <i className="fa-solid fa-spinner fa-spin"></i>
    </div>
  );
  if (error) return <div className="ocd-wrapper" style={{ textAlign: 'center' }}>{error}</div>;
  if (!contract) return <div className="ocd-wrapper" style={{ textAlign: 'center' }}>Contract not found</div>;

  const currency = contract.currency || 'USD';
  const currencySymbol = getCurrencySymbol(currency);
  const totalAmount = contract.totalAmount || 0;

  return (
    <div className="ocd-wrapper">
      <button className="ocd-btn-back" onClick={() => navigate(-1)}>
        <i className="fa-solid fa-arrow-left"></i> Back
      </button>

      <div className="ocd-header-section">
        <div className="ocd-title">
          <h1><i className="fa-solid fa-file-contract"></i> {contract.vesselName}</h1>
          <p>ID: {contract._id?.slice(-8)}</p>
        </div>
        <div className="ocd-status-badge">
          <i className="fa-solid fa-circle-dot"></i> {getStatusText(contract.status)}
        </div>
      </div>

      <div className="ocd-section">
        <h2 className="ocd-section-title"><i className="fa-solid fa-circle-info"></i> Contract Details</h2>
        <div className="ocd-grid">
          <div className="ocd-info-item">
            <span className="ocd-label">Service Provider</span>
            <span className="ocd-value">{getVendorDisplayName()}</span>
          </div>
          <div className="ocd-info-item">
            <span className="ocd-label">Service Type</span>
            <span className="ocd-value">{contract.serviceType}</span>
          </div>
          <div className="ocd-info-item">
            <span className="ocd-label">Contract Value</span>
            <span className="ocd-value-highlight">
              {currencySymbol}{totalAmount.toLocaleString()} {currency}
            </span>
          </div>
        </div>

        {(contract.status === 'work_in_progress' || contract.status === 'work_completed') && (
          <div className="ocd-progress-wrap">
            <div className="ocd-progress-header">
              <span>Work Progress</span>
              <span style={{ color: '#18cb96' }}>{contract.progress || 0}%</span>
            </div>
            <div className="ocd-progress-bar">
              <div className="ocd-progress-fill" style={{ width: `${contract.progress || 0}%` }}></div>
            </div>
          </div>
        )}
      </div>

      {(contract.status === 'pending_payment' ||
        contract.status === 'payment_uploaded' ||
        contract.status === 'payment_verified') && (
        <div className="ocd-alert-box">
          <i className="fa-solid fa-circle-check"></i>
          <p>Contract is active. The service provider can now begin work. Communicate with them via messages to finalize any details.</p>
          <button
            className="ocd-btn-primary"
            style={{ width: 'auto' }}
            onClick={() => navigate(`/messages/${getVendorId()}`)}
          >
            <i className="fa-solid fa-comments"></i> Message Provider
          </button>
        </div>
      )}

      {contract.status === 'work_in_progress' && (
        <div className="ocd-alert-box">
          <i className="fa-solid fa-hammer"></i>
          <p>Work is currently in progress. You will be notified when the service provider marks it complete.</p>
          <button
            className="ocd-btn-primary"
            style={{ width: 'auto' }}
            onClick={() => navigate(`/messages/${getVendorId()}`)}
          >
            <i className="fa-solid fa-comments"></i> Message Provider
          </button>
        </div>
      )}

      {contract.status === 'work_completed' && (
        <div className="ocd-alert-box">
          <i className="fa-solid fa-list-check"></i>
          <p style={{ marginBottom: '16px' }}>
            Service provider has marked the job as completed. Please review and confirm to close the contract.
          </p>
          <button
            className="ocd-btn-primary"
            style={{ width: 'auto' }}
            onClick={handleConfirmComplete}
          >
            <i className="fa-solid fa-unlock-keyhole"></i> Confirm Completion
          </button>
        </div>
      )}

      {contract.status === 'payment_released' && (
        <div className="ocd-alert-box">
          <i className="fa-solid fa-circle-check"></i>
          <p>Contract has been completed. Thank you for using SeaZex!</p>
          {!hasRated && (
            <button
              className="ocd-btn-primary"
              onClick={() => setShowRatingModal(true)}
              style={{ marginTop: '16px' }}
            >
              <i className="fa-regular fa-star"></i> Rate Provider
            </button>
          )}
          {hasRated && (
            <p style={{ marginTop: '12px', fontSize: '12px', color: '#10b981' }}>
              <i className="fas fa-check-circle"></i> Thank you for your rating!
            </p>
          )}
        </div>
      )}

      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        contractId={contract._id}
        vesselName={contract.vesselName}
        vendorName={getVendorDisplayName()}
        onSuccess={() => { setHasRated(true); }}
      />
    </div>
  );
};

export default OwnerContractDetail;